import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  optionSetsApi,
  type CreateOptionSetPayload,
  type OptionSetListParams,
  type UpdateOptionSetPayload,
} from '@/api/optionSetsApi'

export type { OptionSetListParams }

/** Prefix key used for invalidating all Option Set queries (list + usage). */
const OPTION_SETS_KEY = ['optionSets'] as const

/** List query + create/update/delete mutations for Option Sets. */
export function useOptionSets(params: OptionSetListParams = {}) {
  const qc = useQueryClient()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: OPTION_SETS_KEY })
  }

  const list = useQuery({
    queryKey: ['optionSets', 'list', params],
    queryFn: () => optionSetsApi.list(params),
    placeholderData: keepPreviousData,
  })

  const create = useMutation({
    mutationFn: (payload: CreateOptionSetPayload) => optionSetsApi.create(payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: UpdateOptionSetPayload }) =>
      optionSetsApi.update(input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => optionSetsApi.remove(id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}

/** Read-only list query — use in contexts that do not need mutations (e.g. the form-builder picker). */
export function useOptionSetsList(params: OptionSetListParams = {}) {
  return useQuery({
    queryKey: ['optionSets', 'list', params],
    queryFn: () => optionSetsApi.list(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * Fetches exactly the option sets identified by `ids` (comma-separated via the
 * `ids` query param the backend supports). Returns `items` directly so callers
 * don't need to unwrap. Disabled and returns [] when the ids array is empty so
 * React Query never fires an empty request.
 */
export function useOptionSetsByIds(ids: string[]) {
  const joined = ids.join(',')
  const query = useQuery({
    queryKey: ['optionSets', 'byIds', joined],
    queryFn: () => optionSetsApi.list({ ids: joined }),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
  })
  return ids.length === 0 ? [] : (query.data?.items ?? [])
}

/** Usage query for a single option set — which forms reference it (delete guard). */
export function useOptionSetUsage(id: string | null) {
  return useQuery({
    queryKey: ['optionSets', id, 'usage'],
    queryFn: () => optionSetsApi.getUsage(id as string),
    enabled: !!id,
  })
}
